import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch, authExpiredEvent, readApiError } from "../api/api-client";

type AuthStatus = "authenticated" | "loading" | "unauthenticated";

type AuthContextValue = {
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  status: AuthStatus;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");

  useEffect(() => {
    let disposed = false;
    void fetch("/api/auth/session")
      .then(async (response) => {
        if (!disposed) {
          const body = (await response.json()) as { authenticated?: boolean };
          setStatus(body.authenticated ? "authenticated" : "unauthenticated");
        }
      })
      .catch(() => {
        if (!disposed) {
          setStatus("unauthenticated");
        }
      });

    const handleExpired = () => setStatus("unauthenticated");
    window.addEventListener(authExpiredEvent, handleExpired);
    return () => {
      disposed = true;
      window.removeEventListener(authExpiredEvent, handleExpired);
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      async login(password) {
        const response = await fetch("/api/auth/login", {
          body: JSON.stringify({ password }),
          headers: { "Content-Type": "application/json" },
          method: "POST"
        });
        if (!response.ok) {
          throw new Error(await readApiError(response, "登录失败"));
        }
        setStatus("authenticated");
      },
      async logout() {
        try {
          await apiFetch("/api/auth/logout", { method: "POST" });
        } finally {
          setStatus("unauthenticated");
        }
      },
      status
    }),
    [status]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
